import React, { useState, useEffect } from 'react';
import { authFetch } from '../services/api';
import './DataExportReports.css';
import './IntegrationApplicationMasterData.css';

interface Application {
  id: string;
  name: string;
  applicationOwner: string;
  technologyStack: string;
  businessDomain: string;
  businessCriticality: string;
  userCount: string | number;
  overallScore: number;
  recommendation: 'Retire' | 'Re-Platform' | 'Re-Design' | 'Retain' | 'Pending';
  completionPercentage: number;
  completedDate: string | null;
  totalQuestions: number;
  answeredQuestions: number;
  assessmentStatus: 'Completed' | 'In-Process' | 'Not Started';
}

interface APRModelGroup {
  modelId: string;
  modelName: string;
  modelStatus: string;
  modelCreatedAt: string;
  applications: Application[];
  selectedApps: Set<string>;
}

interface ExportFilter {
  applicationIds: string[];
  modelIds: string[];
  recommendations: string[];
  dateRange: {
    from: string;
    to: string;
  };
  includeDetails: boolean;
}

interface ScoringThreshold {
  id: string;
  decision_name: string;
  min_score: number;
  max_score: number;
  color: string;
  description: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

interface ApplicationCode {
  id: string;
  application_id_code: string;
  name: string;
}

const DataExportReports: React.FC = () => {
  const [modelGroups, setModelGroups] = useState<APRModelGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<string | null>(null);
  const [filters] = useState<ExportFilter>({
    applicationIds: [],
    modelIds: [],
    recommendations: [],
    dateRange: { from: '', to: '' },
    includeDetails: false
  });
  const [totalSelected, setTotalSelected] = useState(0);
  const [scoringThresholds, setScoringThresholds] = useState<ScoringThreshold[]>([]);
  const [applicationCodes, setApplicationCodes] = useState<Map<string, string>>(new Map());
  const [collapsedModels, setCollapsedModels] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadAPRModelsWithApplications();
    loadScoringThresholds();
    loadApplicationCodes();
  }, []);

  const loadApplicationCodes = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/applications', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const applications: ApplicationCode[] = await response.json();
        const codeMap = new Map<string, string>();
        applications.forEach(app => {
          if (app.application_id_code) {
            codeMap.set(app.id, app.application_id_code);
          }
        });
        console.log('🏷️ Loaded application codes:', codeMap);
        setApplicationCodes(codeMap);
      }
    } catch (error) {
      console.error('Failed to load application codes:', error);
    }
  };

  const loadScoringThresholds = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/scoring-thresholds', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const thresholds = await response.json();
        console.log('🎨 Loaded scoring thresholds:', thresholds);
        setScoringThresholds(thresholds);
      }
    } catch (error) {
      console.error('Failed to load scoring thresholds:', error);
    }
  };

  const loadAPRModelsWithApplications = async () => {
    setLoading(true);
    try {
      console.log('🚀 STARTING: Loading APR Models with applications...');
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/export/models-with-applications', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📡 Response status:', response.status, response.statusText);
      console.log('📡 Response OK:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Response error text:', errorText);
        throw new Error(`Failed to load APR models: ${response.statusText}`);
      }

      const modelData: APRModelGroup[] = await response.json();
      console.log('✅ RAW API response:', modelData);
      console.log('✅ API response length:', modelData.length);
      console.log('✅ API response type:', typeof modelData, Array.isArray(modelData));
      console.log('✅ First model data:', modelData[0]);

      // Add selectedApps set to each model group
      const modelGroups: APRModelGroup[] = modelData.map(model => ({
        ...model,
        selectedApps: new Set<string>()
      }));

      console.log('✅ Model groups created:', modelGroups);
      console.log('✅ Model groups length:', modelGroups.length);
      console.log('✅ First model group:', modelGroups[0]);
      console.log('✅ First model group applications:', modelGroups[0]?.applications);
      console.log('✅ ABOUT TO SET MODEL GROUPS');
      setModelGroups(modelGroups);

      // Initially collapse all models that have scored applications
      const modelIdsToCollapse = modelGroups
        .filter(group => group.applications.some(app => app.assessmentStatus === 'Completed' && app.overallScore > 0))
        .map(group => group.modelId);
      setCollapsedModels(new Set(modelIdsToCollapse));
      console.log('✅ MODEL GROUPS SET!');

      const totalApps = modelGroups.reduce((sum, model) => sum + model.applications.length, 0);
      console.log(`✅ SUCCESS: Loaded ${modelGroups.length} APR models with ${totalApps} total applications`);
    } catch (error) {
      console.error('❌ ERROR loading APR models with applications:', error);
      setModelGroups([]);
    } finally {
      console.log('🏁 FINISHED: Setting loading to false');
      setLoading(false);
    }
  };

  const handleApplicationSelection = (modelId: string, appId: string, selected: boolean) => {
    console.log('🔍 Application selection:', { modelId, appId, selected });
    setModelGroups(prev => prev.map(group => {
      if (group.modelId === modelId) {
        const newSelectedApps = new Set(group.selectedApps);
        if (selected) {
          newSelectedApps.add(appId);
        } else {
          newSelectedApps.delete(appId);
        }
        return { ...group, selectedApps: newSelectedApps };
      }
      return group;
    }));
  };

  const handleSelectAll = () => {
    setModelGroups(prev => prev.map(group => ({
      ...group,
      selectedApps: new Set(group.applications
        .filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0)
        .map(app => app.id))
    })));
  };

  const handleUnselectAll = () => {
    setModelGroups(prev => prev.map(group => ({
      ...group,
      selectedApps: new Set()
    })));
  };

  const handleModelSelection = (modelId: string, selected: boolean) => {
    setModelGroups(prev => prev.map(group => {
      if (group.modelId === modelId) {
        const scoredApps = group.applications.filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0);
        return {
          ...group,
          selectedApps: selected ? new Set(scoredApps.map(app => app.id)) : new Set()
        };
      }
      return group;
    }));
  };

  const isModelFullySelected = (group: APRModelGroup): boolean => {
    const scoredApps = group.applications.filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0);
    return scoredApps.length > 0 && scoredApps.every(app => group.selectedApps.has(app.id));
  };

  const isModelPartiallySelected = (group: APRModelGroup): boolean => {
    const scoredApps = group.applications.filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0);
    const selectedCount = scoredApps.filter(app => group.selectedApps.has(app.id)).length;
    return selectedCount > 0 && selectedCount < scoredApps.length;
  };

  // Update total selected count whenever modelGroups changes
  useEffect(() => {
    const total = modelGroups.reduce((sum, group) => sum + group.selectedApps.size, 0);
    setTotalSelected(total);
  }, [modelGroups]);

  // Toggle collapse/expand for a single model
  const toggleModelCollapse = (modelId: string) => {
    setCollapsedModels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(modelId)) {
        newSet.delete(modelId);
      } else {
        newSet.add(modelId);
      }
      return newSet;
    });
  };

  // Expand all models
  const handleExpandAll = () => {
    setCollapsedModels(new Set());
  };

  // Collapse all models
  const handleCollapseAll = () => {
    const allModelIds = modelGroups
      .filter(group => group.applications.some(app => app.assessmentStatus === 'Completed' && app.overallScore > 0))
      .map(group => group.modelId);
    setCollapsedModels(new Set(allModelIds));
  };

  const handleExport = async (exportType: 'executive' | 'models' | 'applications' | 'assessments') => {
    const selectedAppIds = getAllSelectedApplicationIds();
    console.log('🔍 Export requested:', { exportType, selectedAppIds, totalSelected });
    console.log('🔍 Model groups state:', modelGroups.map(g => ({
      modelId: g.modelId,
      modelName: g.modelName,
      selectedApps: Array.from(g.selectedApps),
      selectedCount: g.selectedApps.size
    })));

    if (selectedAppIds.length === 0) {
      alert('Please select at least one application to export');
      return;
    }

    // Show confirmation dialog
    const exportTypeNames = {
      'executive': 'Executive Summary',
      'models': 'APR Model Reports', 
      'applications': 'Application Details',
      'assessments': 'Assessment Deep Dive'
    };

    const selectedModels = modelGroups.filter(group => group.selectedApps.size > 0).length;
    const confirmMessage = `🔍 Export Confirmation\n\n` +
      `Export Type: ${exportTypeNames[exportType]}\n` +
      `Selected Applications: ${selectedAppIds.length}\n` +
      `From APR Models: ${selectedModels}\n\n` +
      `This will generate an Excel file with your selected data.\n\n` +
      `Do you want to proceed with the download?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setExporting(exportType);
    try {
      const exportData = {
        applicationIds: selectedAppIds,
        exportType,
        filters: {
          ...filters,
          applicationIds: selectedAppIds
        },
        includeDetails: filters.includeDetails
      };

      console.log('Exporting data for', selectedAppIds.length, 'applications');
      
      // Use direct fetch call since apiService doesn't have export endpoints
      const token = localStorage.getItem('authToken');
      const response = await authFetch('/api/export/excel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(exportData)
      });
      
      if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
      }

      // Handle blob response
      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `APR_${exportType}_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log(`✅ Export completed - ${exportType} with ${selectedAppIds.length} applications`);

    } catch (error) {
      console.error('Export error:', error);
      alert('Export failed. Please try again.');
    } finally {
      setExporting(null);
    }
  };

  const getAllSelectedApplicationIds = (): string[] => {
    return modelGroups.reduce((ids: string[], group) => {
      return ids.concat(Array.from(group.selectedApps));
    }, []);
  };



  console.log('🎬 RENDER CYCLE - Loading:', loading, 'ModelGroups:', modelGroups, 'Length:', modelGroups.length);

  if (loading) {
    console.log('⏳ SHOWING LOADING SCREEN');
    return (
      <div className="export-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <h3>Loading APR Models...</h3>
          <p>Please wait while we fetch your data export options.</p>
        </div>
      </div>
    );
  }

  console.log('✅ NOT LOADING - About to render main content');
  console.log('🔍 About to render, modelGroups:', modelGroups);
  console.log('🔍 Model groups length in render:', modelGroups.length);

  if (modelGroups.length === 0) {
    console.log('⚠️ NO MODEL GROUPS - Showing empty state');
  } else {
    console.log('✅ HAVE MODEL GROUPS - Should show data table');
  }

  // Helper functions for styling
  const getRecommendationColor = (recommendation: string | null | undefined): string => {
    if (!recommendation || recommendation === 'Pending') {
      return '#9e9e9e';
    }
    
    console.log('🔍 Looking for color for recommendation:', recommendation);
    console.log('🔍 Available thresholds:', scoringThresholds);
    
    // Find the threshold that matches this recommendation name
    const threshold = scoringThresholds.find(t => 
      t.decision_name && t.decision_name.toLowerCase() === recommendation.toLowerCase()
    );
    
    console.log('🎨 Found threshold:', threshold);
    return threshold?.color || '#9e9e9e';
  };

  const getRecommendationIcon = (recommendation: string): string => {
    const icons: Record<string, string> = {
      'Retire': '🗑️',
      'Re-Platform': '🔧',
      'Re-Design': '🎨', 
      'Retain': '✅',
      'Pending': '⏳'
    };
    return icons[recommendation] || '❓';
  };

  const formatScore = (score: number | null | undefined): string => {
    if (score === null || score === undefined) return 'N/A';
    return score.toFixed(2);
  };

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Data Export & Reports</h1>
            <p className="page-header__subtitle">
              Export application portfolio data, assessments, and analytics to Excel for reporting and analysis
            </p>
          </div>
          <div className="page-header__actions">
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">
      {/* Export Options */}
      <div className="export-content">
        <div className="export-options">
          <div className="export-cards">
            <div className="export-card">
              <div className="export-card-header">
                <span className="export-card-icon">📈</span>
                <h3>Executive Summary</h3>
              </div>
              <div className="export-card-content">
                <p>High-level dashboard metrics, 4R distribution, and model status overview</p>
                <ul>
                  <li>Portfolio health metrics</li>
                  <li>Recommendation distribution</li>
                  <li>Model completion status</li>
                </ul>
              </div>
              <button
                className="export-btn export-btn--executive"
                onClick={() => handleExport('executive')}
                disabled={totalSelected === 0 || exporting === 'executive'}
              >
                {exporting === 'executive' ? 'Exporting...' : 'Export Summary'}
              </button>
            </div>

            <div className="export-card">
              <div className="export-card-header">
                <span className="export-card-icon">🏢</span>
                <h3>APR Model Reports</h3>
              </div>
              <div className="export-card-content">
                <p>Detailed model-level data with application summaries and scores</p>
                <ul>
                  <li>Model details and timeline</li>
                  <li>Application summaries</li>
                  <li>Dimension score averages</li>
                </ul>
              </div>
              <button
                className="export-btn export-btn--models"
                onClick={() => handleExport('models')}
                disabled={totalSelected === 0 || exporting === 'models'}
              >
                {exporting === 'models' ? 'Exporting...' : 'Export Models'}
              </button>
            </div>

            <div className="export-card">
              <div className="export-card-header">
                <span className="export-card-icon">📋</span>
                <h3>Application Details</h3>
              </div>
              <div className="export-card-content">
                <p>Complete application data with scores, recommendations, and metadata</p>
                <ul>
                  <li>Application master data</li>
                  <li>Dimension & component scores</li>
                  <li>4R recommendations</li>
                </ul>
              </div>
              <button
                className="export-btn export-btn--applications"
                onClick={() => handleExport('applications')}
                disabled={totalSelected === 0 || exporting === 'applications'}
              >
                {exporting === 'applications' ? 'Exporting...' : 'Export Applications'}
              </button>
            </div>

            <div className="export-card">
              <div className="export-card-header">
                <span className="export-card-icon">❓</span>
                <h3>Assessment Deep Dive</h3>
              </div>
              <div className="export-card-content">
                <p>Full Q&A responses, scoring breakdown, and assessment analytics</p>
                <ul>
                  <li>Individual question responses</li>
                  <li>Detailed scoring calculations</li>
                  <li>Assessment timeline data</li>
                </ul>
              </div>
              <button
                className="export-btn export-btn--assessments"
                onClick={() => handleExport('assessments')}
                disabled={totalSelected === 0 || exporting === 'assessments'}
              >
                {exporting === 'assessments' ? 'Exporting...' : 'Export Assessments'}
              </button>
            </div>
          </div>
        </div>

        {/* Application Selection */}
        <div className="model-selection">
          <div className="selection-header">
            <h2>🎯 Select Applications</h2>
            <div className="selection-actions">
              <div className="selection-buttons">
                <button
                  className="select-all-btn"
                  onClick={handleSelectAll}
                >
                  Select All
                </button>
                <button
                  className="unselect-all-btn"
                  onClick={handleUnselectAll}
                >
                  Unselect All
                </button>
                <span className="button-separator">|</span>
                <span className="expand-collapse-controls">
                  <button
                    className="icon-btn"
                    onClick={handleExpandAll}
                    title="Expand All"
                  >
                    <span className="icon-arrow">▼</span>
                    <span className="icon-label">Expand All</span>
                  </button>
                  <button
                    className="icon-btn"
                    onClick={handleCollapseAll}
                    title="Collapse All"
                  >
                    <span className="icon-arrow">▲</span>
                    <span className="icon-label">Collapse All</span>
                  </button>
                </span>
              </div>
              <span className="selection-count">
                {totalSelected} of {modelGroups.reduce((sum, group) => sum + group.applications.filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0).length, 0)} selected
              </span>
            </div>
          </div>

          <div className="applications-table-container">
            {modelGroups.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
                <h3>No APR Models Found</h3>
                <p>There are no APR models with scored applications available for export.</p>
                <p>Please complete assessments for applications first.</p>
              </div>
            ) : (
              modelGroups
                .filter(group => group.applications.some(app => app.assessmentStatus === 'Completed' && app.overallScore > 0))
                .map(group => {
                  const scoredApps = group.applications.filter(app => app.assessmentStatus === 'Completed' && app.overallScore > 0);
                  const isFullySelected = isModelFullySelected(group);
                  const isPartiallySelected = isModelPartiallySelected(group);
                  const isCollapsed = collapsedModels.has(group.modelId);

                  return (
                    <div key={group.modelId} className="model-group">
                      {/* Model Header Row - QA Screen Style */}
                      <div
                        className={`model-header-row ${isFullySelected ? 'selected' : ''}`}
                      >
                        <button
                          className="collapse-toggle-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleModelCollapse(group.modelId);
                          }}
                          title={isCollapsed ? 'Expand' : 'Collapse'}
                        >
                          {isCollapsed ? '▶' : '▼'}
                        </button>
                        <div className="model-header-checkbox">
                          <input
                            type="checkbox"
                            checked={isFullySelected}
                            ref={(el) => {
                              if (el) el.indeterminate = isPartiallySelected;
                            }}
                            onChange={(e) => handleModelSelection(group.modelId, e.target.checked)}
                            onClick={(e) => e.stopPropagation()}
                            className="table-checkbox"
                          />
                        </div>
                        <span className="model-header-icon">📁</span>
                        <div className="model-header-info">
                          <span className="model-header-name">{group.modelName}</span>
                        </div>
                        <div className="model-header-stats">
                          <span className="model-stats-badge apps">
                            {scoredApps.length} apps
                          </span>
                          <span className="model-stats-badge selected">
                            {scoredApps.filter(app => group.selectedApps.has(app.id)).length} selected
                          </span>
                          <span className={`model-status-badge ${group.modelStatus.toLowerCase().replace(/\s+/g, '-')}`}>
                            {group.modelStatus}
                          </span>
                        </div>
                      </div>

                      {/* Applications Table - Collapsible */}
                      {!isCollapsed && (
                        <>
                          {/* Applications Table Header */}
                          <div className="applications-table-header">
                            <div className="table-header-cell checkbox-col"></div>
                            <div className="table-header-cell">Application ID</div>
                            <div className="table-header-cell">Application Name</div>
                            <div className="table-header-cell">Recommendation</div>
                            <div className="table-header-cell">Score</div>
                          </div>

                          {/* Applications Rows */}
                          <div className="applications-table-body">
                        {scoredApps.map(app => {
                          const isSelected = group.selectedApps.has(app.id);
                          const displayId = applicationCodes.get(app.id) || app.name || 'N/A';

                          return (
                            <div
                              key={app.id}
                              className={`table-row ${isSelected ? 'selected' : ''}`}
                              onClick={() => handleApplicationSelection(group.modelId, app.id, !isSelected)}
                            >
                              <div className="table-cell checkbox-col">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => handleApplicationSelection(group.modelId, app.id, e.target.checked)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="table-checkbox"
                                />
                              </div>
                              <div className="table-cell app-id-col">
                                <span className="app-id-text">{displayId}</span>
                              </div>
                              <div className="table-cell app-name-col">
                                <span className="app-name">{app.name}</span>
                              </div>
                              <div className="table-cell recommendation-col">
                                <span
                                  className="recommendation-badge-dynamic"
                                  style={{
                                    backgroundColor: getRecommendationColor(app.recommendation),
                                    color: 'white'
                                  }}
                                >
                                  {app.recommendation || 'Pending'}
                                </span>
                              </div>
                              <div className="table-cell score-col">
                                <div className="score-info-reports">
                                  <div className="score-value-reports">{formatScore(app.overallScore)}/5.0</div>
                                  <div className="score-bar-reports">
                                    <div
                                      className="score-bar-fill-reports"
                                      style={{
                                        width: `${(app.overallScore / 5) * 100}%`,
                                        backgroundColor: getRecommendationColor(app.recommendation)
                                      }}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })
            )}
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default DataExportReports;